import React, { useRef, useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Upload, CheckCircle2, AlertTriangle, Loader2, ArrowRight, UserPlus,
  Radio, History, Undo2, FileSpreadsheet,
} from "lucide-react";
import DriverFormDialog from "@/components/DriverFormDialog";

const fetcher = (url) => api.get(url).then((r) => r.data);

const StatusCard = ({ label, value, sub, tone = "default", icon: Icon }) => {
  const tones = {
    default: "border-brand-border",
    success: "border-emerald-200 bg-emerald-50/40",
    warn: "border-amber-200 bg-amber-50/40",
    danger: "border-red-200 bg-red-50/40",
  };
  return (
    <Card className={`p-5 rounded-2xl ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-brand-text-muted">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="font-heading text-2xl md:text-3xl font-bold mt-2 text-brand-text">
        {value ?? "—"}
      </div>
      {sub && <div className="text-xs text-brand-text-muted mt-1">{sub}</div>}
    </Card>
  );
};

const DpdImport = () => {
  const { data: periods } = useSWR("/pay-periods", fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const { data: status } = useSWR("/dpd-import/status", fetcher, { refreshInterval: 30000 });
  const { data: history } = useSWR("/dpd-import/history", fetcher);

  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [periodId, setPeriodId] = useState("");
  const [busy, setBusy] = useState(false);
  const [mappings, setMappings] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [prefillCode, setPrefillCode] = useState("");
  const [prefillRowIdx, setPrefillRowIdx] = useState(null);
  const [rollbackTarget, setRollbackTarget] = useState(null);

  const openCreateDriver = (rowIdx, code) => {
    setPrefillRowIdx(rowIdx);
    setPrefillCode(code || "");
    setCreateOpen(true);
  };

  const handleDriverSaved = async (newDriver) => {
    await mutate("/drivers");
    if (prefillRowIdx !== null && newDriver?.id) {
      setMappings((m) => ({ ...m, [prefillRowIdx]: newDriver.id }));
      toast.success(`Row ${prefillRowIdx + 1} → assigned to ${newDriver.full_name}`);
    }
    setPrefillCode("");
    setPrefillRowIdx(null);
  };

  const sortedValid = useMemo(() => {
    if (!preview?.valid) return [];
    return [...preview.valid].sort((a, b) => {
      const da = a.work_date || ""; const db = b.work_date || "";
      if (da !== db) return da.localeCompare(db);
      return (a.driver_code || "").localeCompare(b.driver_code || "");
    });
  }, [preview]);

  const sortedInvalid = useMemo(() => {
    if (!preview?.invalid) return [];
    return [...preview.invalid].sort((a, b) =>
      (a.driver_code || "").localeCompare(b.driver_code || "") ||
      (a.work_date || "").localeCompare(b.work_date || "")
    );
  }, [preview]);

  React.useEffect(() => {
    if (periods && !periodId) {
      const open = periods.find((p) => p.status === "open");
      if (open) setPeriodId(open.id);
    }
  }, [periods, periodId]);

  const doPreview = async (f) => {
    setBusy(true);
    setMappings({});
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/dpd-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setBusy(false);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    doPreview(f);
  };

  const doConfirm = async () => {
    if (!file || !periodId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("pay_period_id", periodId);
      fd.append("manual_mappings", JSON.stringify(mappings));
      const { data } = await api.post("/dpd-import/confirm", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Imported ${data.inserted}` +
        (data.reassigned ? ` (${data.reassigned} redirected)` : "") +
        `. Skipped ${data.skipped}.`
      );
      setFile(null);
      setPreview(null);
      setMappings({});
      if (fileRef.current) fileRef.current.value = "";
      await Promise.all([
        mutate("/dpd-import/history"),
        mutate("/dpd-import/status"),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const doRollback = async () => {
    if (!rollbackTarget) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dpd-import/${rollbackTarget.id}/rollback`);
      toast.success(`Rolled back ${data.deleted} row(s) from ${rollbackTarget.filename}`);
      setRollbackTarget(null);
      await Promise.all([
        mutate("/dpd-import/history"),
        mutate("/dpd-import/status"),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
    setBusy(false);
  };

  const reassignedCount = Object.keys(mappings).filter((k) => mappings[k]).length;
  const totalReadyToImport = (preview?.valid_count || 0) + reassignedCount;

  const lastImportSub = status?.last_import
    ? `${status.last_import.filename} · ${ddmmyyyy(status.last_import.created_at?.slice(0, 10))}`
    : "No imports yet";

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Ingest · DPD Sync Center</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">DPD Import</h1>
        <p className="text-sm text-brand-text-muted mt-1">
          Upload the DPD Transaction Payments export. Columns auto-map: <span className="font-mono">driver_code, date, route, stops, parcels, collections, route_payment</span>.
        </p>
      </div>

      {/* Sync Status Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dpd-status-grid">
        <StatusCard
          icon={Radio}
          label="Connection"
          value="CSV Upload"
          sub="API sync coming soon"
          tone="success"
        />
        <StatusCard
          icon={FileSpreadsheet}
          label="Last Import"
          value={status?.last_import ? `+${status.last_import.inserted}` : "—"}
          sub={lastImportSub}
          tone={status?.last_import ? "default" : "warn"}
        />
        <StatusCard
          icon={CheckCircle2}
          label="Rows in DB (DPD)"
          value={status?.total_imported_rows ?? 0}
          sub={`${status?.total_imports ?? 0} total imports`}
        />
        <StatusCard
          icon={AlertTriangle}
          label="Unmatched (last)"
          value={status?.unmatched_last_import ?? 0}
          sub="Fix via redirect below"
          tone={status?.unmatched_last_import ? "danger" : "default"}
        />
      </div>

      <Card className="p-6 rounded-2xl border-brand-border">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-brand-text-muted">File</label>
            <div className="mt-1.5 flex items-center gap-3">
              <input
                ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
                onChange={onFile} data-testid="dpd-file-input"
                className="text-sm file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-brand-surface file:text-brand-text file:cursor-pointer"
              />
              {busy && <Loader2 className="w-4 h-4 animate-spin text-brand-text-muted" />}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-brand-text-muted">Target pay period</label>
            <Select value={periodId} onValueChange={setPeriodId}>
              <SelectTrigger className="mt-1.5" data-testid="dpd-period-select"><SelectValue placeholder="Select period" /></SelectTrigger>
              <SelectContent>
                {(periods || []).map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled={p.status !== "open"}>
                    {p.name} · {p.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {preview && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-5 rounded-2xl border-brand-border">
            <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Total rows</div>
            <div className="font-heading text-3xl font-bold mt-2">{preview.total_rows}</div>
          </Card>
          <Card className="p-5 rounded-2xl border-emerald-200 bg-emerald-50/50">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700">Ready to import</div>
            <div className="font-heading text-3xl font-bold mt-2 text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />{totalReadyToImport}
              {reassignedCount > 0 && (
                <span className="text-[11px] font-normal text-emerald-600">
                  ({preview.valid_count} auto + {reassignedCount} redirected)
                </span>
              )}
            </div>
          </Card>
          <Card className="p-5 rounded-2xl border-red-200 bg-red-50/50">
            <div className="text-[10px] uppercase tracking-wider text-red-700">Needs attention</div>
            <div className="font-heading text-3xl font-bold mt-2 text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />{preview.invalid_count - reassignedCount}
            </div>
          </Card>
        </div>
      )}

      {/* Reassignment panel */}
      {preview && preview.invalid.length > 0 && (
        <Card className="rounded-2xl border-amber-200 overflow-hidden">
          <div className="p-4 border-b border-amber-100 bg-amber-50/60 flex items-center gap-2 text-sm">
            <ArrowRight className="w-4 h-4 text-amber-700" />
            <span className="text-amber-900 font-medium">Redirect payment to a driver</span>
            <span className="text-brand-text-muted text-xs">— pick the correct driver for each row below and it'll be included in the import</span>
          </div>
          <div className="divide-y divide-brand-border">
            {sortedInvalid.slice(0, 30).map((r) => (
              <div key={r.row_index} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`invalid-row-${r.row_index}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-brand-text-muted">Row {r.row_index + 1} · {ddmmyyyy(r.work_date) || "—"} · {r.route || "no route"}</div>
                  <div className="text-sm">
                    <span className="font-mono text-red-600">{r.driver_code || "(no code)"}</span>
                    <span className="mx-2 text-brand-text-muted">→</span>
                    <span className="text-brand-text-muted">{r.stops} stops · {r.parcels} parcels · </span>
                    <span className="font-heading font-bold text-brand-text">{gbp(r.route_payment)}</span>
                  </div>
                  {r.errors.length > 0 && (
                    <div className="text-[11px] text-red-600 mt-1">{r.errors.join(" · ")}</div>
                  )}
                </div>
                <div className="w-full md:w-72 flex items-center gap-2">
                  <Select
                    value={mappings[r.row_index] || ""}
                    onValueChange={(v) =>
                      setMappings((m) => ({ ...m, [r.row_index]: v === "__none" ? undefined : v }))
                    }
                  >
                    <SelectTrigger data-testid={`reassign-${r.row_index}`}>
                      <SelectValue placeholder="Assign to driver…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Skip this row —</SelectItem>
                      {(drivers || []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.full_name} ({d.driver_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => openCreateDriver(r.row_index, r.driver_code)}
                    data-testid={`create-driver-${r.row_index}`}
                    title="Create a new driver for this row"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <DriverFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        driver={prefillCode ? { driver_code: prefillCode } : null}
        onSaved={handleDriverSaved}
      />

      {/* Valid preview */}
      {preview && preview.valid.length > 0 && (
        <Card className="rounded-2xl border-brand-border overflow-hidden">
          <div className="p-4 border-b border-brand-border flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="text-sm font-medium">Preview — first {Math.min(preview.valid.length, 15)} auto-matched rows</div>
            <Button
              onClick={doConfirm} disabled={busy || !periodId || totalReadyToImport === 0}
              className="bg-brand-red hover:bg-brand-red-hover"
              data-testid="dpd-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Import {totalReadyToImport} row(s)
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Driver</th>
                  <th className="text-left px-4 py-3">Route</th>
                  <th className="text-right px-4 py-3">Stops</th>
                  <th className="text-right px-4 py-3">Parcels</th>
                  <th className="text-right px-4 py-3">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {sortedValid.slice(0, 15).map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 font-mono text-xs">{ddmmyyyy(r.work_date)}</td>
                    <td className="px-4 py-2 text-xs">{r.driver_name} <span className="font-mono text-brand-text-muted">({r.driver_code})</span></td>
                    <td className="px-4 py-2 font-mono text-xs">{r.route}</td>
                    <td className="px-4 py-2 text-right">{r.stops}</td>
                    <td className="px-4 py-2 text-right">{r.parcels}</td>
                    <td className="px-4 py-2 text-right font-heading font-bold">{gbp(r.route_payment)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {preview && preview.valid.length === 0 && preview.invalid.length === 0 && (
        <Card className="p-8 rounded-2xl border-brand-border text-center text-sm text-brand-text-muted">
          File uploaded but no recognisable rows were found. Check that the DPD export has the expected columns.
        </Card>
      )}

      {/* Import history */}
      <Card className="rounded-2xl border-brand-border overflow-hidden" data-testid="dpd-history-card">
        <div className="p-4 border-b border-brand-border flex items-center gap-2">
          <History className="w-4 h-4 text-brand-text-muted" />
          <div className="text-sm font-medium">Import history</div>
          <div className="text-xs text-brand-text-muted ml-auto">
            {history?.length || 0} batch{(history?.length || 0) === 1 ? "" : "es"}
          </div>
        </div>
        {(!history || history.length === 0) ? (
          <div className="p-8 text-center text-sm text-brand-text-muted">No imports yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">File</th>
                  <th className="text-right px-4 py-3">Inserted</th>
                  <th className="text-right px-4 py-3">Redirected</th>
                  <th className="text-right px-4 py-3">Skipped</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {history.map((h) => (
                  <tr key={h.id} data-testid={`history-row-${h.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{ddmmyyyy(h.created_at?.slice(0, 10))}</td>
                    <td className="px-4 py-2 text-xs">{h.filename}</td>
                    <td className="px-4 py-2 text-right font-medium text-emerald-700">{h.inserted}</td>
                    <td className="px-4 py-2 text-right">{h.reassigned || 0}</td>
                    <td className="px-4 py-2 text-right text-brand-text-muted">{h.skipped}</td>
                    <td className="px-4 py-2 text-xs">
                      {h.rolled_back_at ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                          Rolled back
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!h.rolled_back_at && h.inserted > 0 && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setRollbackTarget(h)}
                          data-testid={`rollback-btn-${h.id}`}
                        >
                          <Undo2 className="w-3.5 h-3.5 mr-1" /> Rollback
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AlertDialog open={!!rollbackTarget} onOpenChange={(o) => !o && setRollbackTarget(null)}>
        <AlertDialogContent data-testid="rollback-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rollback this import?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <b>{rollbackTarget?.inserted}</b> work row(s) inserted from{" "}
              <b>{rollbackTarget?.filename}</b>. Invoices already generated from these rows may become inaccurate.
              This action is audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rollback-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRollback}
              className="bg-brand-red hover:bg-brand-red-hover"
              data-testid="rollback-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />}
              Rollback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DpdImport;
